'use client';
import { useMask } from '@react-input/mask';
import {
  generateLeadURL,
  getCookieGoogleId,
  getCookieMsclkid
} from 'app/actions/affiliate.actions';
import { siteVerification } from 'app/actions/cloudflare.actions';
import { getTurnstileKey } from 'app/actions/turnstile.actions';
import { ButtonGreen } from 'components/buttons/default-green';
import { Modal } from 'flowbite-react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { memo, useEffect, useRef, useState } from 'react';

// Dynamically import Turnstile component with SSR disabled
const Turnstile = dynamic(
  () => import('@marsidev/react-turnstile').then((mod) => ({ default: mod.Turnstile })),
  {
    ssr: false,
    loading: () => <div className="h-[65px] w-full animate-pulse rounded bg-gray-100" />
  }
);

interface DynamicForm {
  [key: string]: string;
}

// Memoized modal component to prevent unnecessary re-renders
const PromotionalModal = memo(({ show, onClose }: { show: boolean; onClose: () => void }) => {
  if (!show) return null;

  return (
    <Modal show={show} onClose={onClose} size="lg" aria-labelledby="promo-modal-title">
      <Modal.Header className="!absolute right-0 z-10 border-none" />
      <Modal.Body>
        <div className="flex flex-col items-center gap-4">
          <h2 id="promo-modal-title" className="text-sm">
            Thank You! Your email with offer details is on the way.
          </h2>
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-black p-5">
            <div className="text-lg font-bold">
              Use Code <span className="text-fp-green">SAVE25</span>
            </div>
            <div className="text-sm font-bold">
              At Checkout for $25 OFF Your Purchase of $250 or More{' '}
            </div>
            <div className="text-sm">Use Your Offer to Buy Now Online</div>
            <ButtonGreen
              href="/shop"
              className="rounded-lg bg-fp-green p-2 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green"
              aria-label="Shop security systems"
            >
              Shop Systems
            </ButtonGreen>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
});

PromotionalModal.displayName = 'PromotionalModal';

export function PromotionBanner2({
  policy,
  sourceKey,
  phoneSourceKey
}: {
  heading: string;
  policy: string;
  sourceKey: string;
  phoneSourceKey: string;
}) {
  const searchParams = useSearchParams();
  // Use localStorage in a way that doesn't affect hydration
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [turnstileLoaded, setTurnstileLoaded] = useState<boolean>(false);
  const [turnstileKey, setTurnstileKey] = useState<string | null>(null);
  const [showPromotionalModal, setShowPromotionalModal] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  // Default form values - only set once to avoid re-renders
  const formRef = useRef<DynamicForm>({
    gaMedium: 'referral',
    gaSource: '',
    gaContent: '/',
    gaClientId: '970385394.1727054325',
    gaCampaign: '(referral)'
  });

  const inputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ }
  });

  // Initialize local storage check in useEffect to avoid hydration issues
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const storedData = localStorage.getItem('hasShownPromoBanner');
    if (storedData) {
      const { timestamp } = JSON.parse(storedData);
      // Check if 24 hours have passed (86400000 milliseconds)
      if (Date.now() - timestamp < 86400000) {
        return;
      }
    }

    setTimeout(() => {
      if (window.innerWidth < 768) {
        localStorage.setItem(
          'hasShownPromoBanner',
          JSON.stringify({
            timestamp: Date.now()
          })
        );
        setIsOpen(true);
      }
    }, 2000);
  }, []);

  // Lazy load Turnstile key only when banner is opened
  useEffect(() => {
    if (isOpen && !turnstileKey) {
      // Small delay to prioritize render
      const timer = setTimeout(() => {
        setTurnstileKey(getTurnstileKey());
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, turnstileKey]);

  // Focus management
  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  // Optimized quote submission with parallel requests
  async function createQuote(turnstileToken: string) {
    if (!turnstileToken) {
      setFormError('Please complete the verification');
      return;
    }

    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    // Use a local loading state to prevent multiple submissions
    let isSubmitting = true;

    try {
      // Execute these requests in parallel to improve performance
      const [status, gclid, msclkid] = await Promise.all([
        siteVerification(turnstileToken),
        getCookieGoogleId(),
        getCookieMsclkid()
      ]);

      if (status === 200) {
        const payload = {
          contact: {
            isResidential: true,
            email
          },
          compliance: {
            tcpaPermissionGranted: true,
            tcpaPermissionLink: ''
          },
          additionalFields: {
            EMAIL: email,
            submissionURL: window.location.href,
            ...formRef.current // Use ref value to avoid re-renders
          } as any
        };

        const subid = searchParams?.get('subid');
        if (subid) {
          payload.additionalFields['subid'] = subid;
        }

        if (gclid !== undefined) payload.additionalFields['googleclickid'] = gclid;
        if (msclkid !== undefined) payload.additionalFields['msclkid'] = msclkid;

        const leadURL = await generateLeadURL(sourceKey);

        if (leadURL) {
          const response = await fetch(leadURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (response.status === 200) {
            // Batch state updates to reduce renders
            setIsOpen(false);
            setTurnstileLoaded(false);
            setEmail('');
            setFormError('');

            // Small delay to ensure the banner is gone before showing modal
            setTimeout(() => {
              setShowPromotionalModal(true);
            }, 100);
          }
        }
      }
    } catch (e) {
      setFormError('An error occurred. Please try again.');
      console.error('Form submission error:', e);
    } finally {
      isSubmitting = false;
    }
  }

  // Optimized phone submission function
  const submitPhone = async () => {
    if (!phone) {
      setFormError('Please enter your phone number');
      return;
    }

    try {
      // Execute these requests in parallel for better performance
      const [gclid, msclkid] = await Promise.all([getCookieGoogleId(), getCookieMsclkid()]);

      const payload = {
        contact: {
          isResidential: true,
          email,
          phone: {
            number: phone
          }
        },
        compliance: {
          tcpaPermissionGranted: true,
          tcpaPermissionLink: ''
        },
        additionalFields: {
          EMAIL: email,
          PHONE: phone,
          submissionURL: window.location.href,
          ...formRef.current // Use ref to avoid re-renders
        } as any
      };

      const subid = searchParams?.get('subid');
      if (subid) {
        payload.additionalFields['subid'] = subid;
      }

      if (gclid) payload.additionalFields['googleclickid'] = gclid;
      if (msclkid) payload.additionalFields['msclkid'] = msclkid;

      const leadURL = await generateLeadURL(phoneSourceKey);

      if (leadURL) {
        const response = await fetch(leadURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.status === 200) {
          // Batch state updates to reduce renders
          setEmail('');
          setPhone('');
          setFormError('');

          // Show promotional modal
          setShowPromotionalModal(true);
        }
      }
    } catch (e) {
      setFormError('An error occurred. Please try again.');
      console.error('Phone submission error:', e);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-between bg-fp-green py-2 lg:flex-row"
      role="banner"
      aria-label="Promotional offer"
    >
      <div className="flex h-full flex-col justify-center text-white">
        <button
          className="h-[33px] text-nowrap rounded-lg border border-white bg-fp-green px-4 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-white"
          onClick={() => setIsOpen(true)}
          onKeyDown={(e) => handleKeyDown(e, () => setIsOpen(true))}
          aria-label="Get promotional offer"
        >
          Get Offer
        </button>
      </div>

      <Modal
        show={isOpen}
        size="lg"
        onClose={() => {
          setIsOpen(false);
          setTurnstileLoaded(false);
          setEmail('');
          setFormError('');
        }}
        aria-labelledby="offer-modal-title"
      >
        <Modal.Header className="!absolute right-0 z-10 border-none" />
        <Modal.Body>
          <div className="my-4 flex flex-col items-center gap-4">
            <h2 id="offer-modal-title" className="text-center text-lg font-bold">
              Enter Your Email to Get an <span className="text-fp-green">Offer</span>
            </h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (email && turnstileKey) {
                  createQuote(turnstileKey);
                }
              }}
            >
              <div className="w-full">
                <div className="flex w-full flex-row items-center rounded-lg border border-gray-300 bg-white">
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="h-[41px] w-[210px] rounded-l-md border-none text-black"
                    name="email"
                    value={email}
                    placeholder="Email address"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={!!formError}
                    aria-describedby={formError ? 'email-error' : undefined}
                  />
                  <button
                    type="submit"
                    className="h-[40px] text-nowrap rounded-lg border border-fp-green bg-fp-green px-4 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green disabled:opacity-50"
                    disabled={!email || !turnstileLoaded}
                    aria-label="Submit email for offer"
                  >
                    Submit
                  </button>
                </div>
                {formError && (
                  <div id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                    {formError}
                  </div>
                )}
              </div>
            </form>

            <div
              className="text-left text-[10px]"
              dangerouslySetInnerHTML={{
                __html: policy
              }}
              aria-label="Terms and conditions"
            />

            {/* Only render Turnstile when needed and visible */}
            {isOpen && turnstileKey && (
              <div aria-label="Verification">
                <Turnstile
                  siteKey={turnstileKey}
                  onSuccess={(token) => {
                    setTurnstileLoaded(true);
                    setFormError('');
                  }}
                  onError={() => {
                    setTurnstileLoaded(false);
                    setFormError('Verification failed. Please try again.');
                  }}
                  options={{
                    size: 'compact',
                    appearance: 'interaction-only'
                  }}
                />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Use memoized modal component to prevent re-renders and improve performance */}
      <PromotionalModal
        show={showPromotionalModal}
        onClose={() => setShowPromotionalModal(false)}
      />
    </div>
  );
}
